import React, { useState, useEffect, useCallback } from 'react';
import { useModal } from '../../components/modal/ModalContext';
import AlertModal from '../../components/modal/AlertModal';
import ConfirmModal from '../../components/modal/ConfirmModal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const formatPence = (pence: number): string =>
  `\u00a3${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const penceToPounds = (pence: number): string => (pence / 100).toFixed(2);
const poundsToPence = (pounds: string): number => Math.round(parseFloat(pounds || '0') * 100);

interface FeeTier {
  id: number;
  min_companies: number;
  max_companies: number;
  fee_pence: number;
  label: string;
}

interface MonthlyCosts {
  settlement_month: string;
  hosting_cost_pence: number;
  stripe_fee_pence: number;
  ai_cost_pence: number;
  other_cost_pence: number;
  notes: string;
}

interface AuditEntry {
  id: number;
  timestamp: string;
  actor: string;
  action: string;
  entity: string;
  before_value: string | null;
  after_value: string | null;
}

type TabKey = 'tiers' | 'costs' | 'invoice' | 'audit';

export default function DeveloperSettings() {
  const { openModal } = useModal();
  const [accessChecked, setAccessChecked] = useState(false);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('tiers');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/billing/developer/check', { headers: authHeaders() });
        const d = await res.json();
        setIsDeveloper(!!d.is_developer_admin);
      } catch {
        setIsDeveloper(false);
      } finally {
        setAccessChecked(true);
      }
    })();
  }, []);

  if (!accessChecked) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!isDeveloper) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-red-300 dark:border-red-700 p-8">
          <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Access Denied</h1>
          <p className="text-slate-500 dark:text-slate-400">
            This page is restricted to authorised platform administrators only.
          </p>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'tiers', label: 'Fee Tiers' },
    { key: 'costs', label: 'Monthly Costs' },
    { key: 'invoice', label: 'Invoice Generation' },
    { key: 'audit', label: 'Audit Log' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Warning Banner */}
      <div className="bg-red-600 dark:bg-red-700 rounded-xl px-5 py-3">
        <p className="text-sm font-semibold text-white text-center">
          Developer Settings &mdash; Restricted to authorised platform administrators only
        </p>
      </div>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Developer Billing Settings</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Manage fee tiers, monthly costs, invoices, and view the audit log.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-1 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'tiers' && <FeeTiersTab />}
      {activeTab === 'costs' && <MonthlyCostsTab />}
      {activeTab === 'invoice' && <InvoiceTab />}
      {activeTab === 'audit' && <AuditLogTab />}
    </div>
  );
}

/* ========== Fee Tiers Tab ========== */

function FeeTiersTab() {
  const { openModal } = useModal();
  const [tiers, setTiers] = useState<FeeTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ min_companies: '', max_companies: '', fee_pounds: '', label: '' });
  const [addingNew, setAddingNew] = useState(false);
  const [newForm, setNewForm] = useState({ min_companies: '', max_companies: '', fee_pounds: '', label: '' });
  const [saving, setSaving] = useState(false);

  const fetchTiers = useCallback(async () => {
    try {
      const res = await fetch('/api/billing/developer/fee-tiers', { headers: authHeaders() });
      const d = await res.json();
      if (d.success && d.tiers) setTiers(d.tiers);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTiers(); }, [fetchTiers]);

  const startEdit = (tier: FeeTier) => {
    setEditingId(tier.id);
    setEditForm({
      min_companies: String(tier.min_companies),
      max_companies: String(tier.max_companies),
      fee_pounds: penceToPounds(tier.fee_pence),
      label: tier.label,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (id: number) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/billing/developer/fee-tiers/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          fee_pence: poundsToPence(editForm.fee_pounds),
          min_companies: parseInt(editForm.min_companies, 10),
          max_companies: parseInt(editForm.max_companies, 10),
          label: editForm.label,
        }),
      });
      const d = await res.json();
      if (d.success !== false) {
        openModal({
          component: AlertModal,
          props: { title: 'Tier Updated', message: 'Fee tier has been updated successfully.', type: 'success' },
        });
        setEditingId(null);
        await fetchTiers();
      } else {
        openModal({
          component: AlertModal,
          props: { title: 'Update Failed', message: d.error || 'Failed to update tier.', type: 'error' },
        });
      }
    } catch {
      openModal({
        component: AlertModal,
        props: { title: 'Error', message: 'Network error updating tier.', type: 'error' },
      });
    } finally {
      setSaving(false);
    }
  };

  const saveNew = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/billing/developer/fee-tiers', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          min_companies: parseInt(newForm.min_companies, 10),
          max_companies: parseInt(newForm.max_companies, 10),
          fee_pence: poundsToPence(newForm.fee_pounds),
          label: newForm.label,
        }),
      });
      const d = await res.json();
      if (d.success !== false) {
        openModal({
          component: AlertModal,
          props: { title: 'Tier Created', message: 'New fee tier has been created successfully.', type: 'success' },
        });
        setAddingNew(false);
        setNewForm({ min_companies: '', max_companies: '', fee_pounds: '', label: '' });
        await fetchTiers();
      } else {
        openModal({
          component: AlertModal,
          props: { title: 'Creation Failed', message: d.error || 'Failed to create tier.', type: 'error' },
        });
      }
    } catch {
      openModal({
        component: AlertModal,
        props: { title: 'Error', message: 'Network error creating tier.', type: 'error' },
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div></div>;
  }

  const inputCls = 'block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Platform Fee Tiers</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">ID</th>
              <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Min Companies</th>
              <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Max Companies</th>
              <th className="text-right py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Fee</th>
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Label</th>
              <th className="text-center py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier) =>
              editingId === tier.id ? (
                <tr key={tier.id} className="border-b border-slate-100 dark:border-slate-700/50 bg-blue-50/50 dark:bg-blue-900/10">
                  <td className="py-3 px-3 text-slate-500 dark:text-slate-400">{tier.id}</td>
                  <td className="py-3 px-3"><input type="number" value={editForm.min_companies} onChange={(e) => setEditForm({ ...editForm, min_companies: e.target.value })} className={inputCls} /></td>
                  <td className="py-3 px-3"><input type="number" value={editForm.max_companies} onChange={(e) => setEditForm({ ...editForm, max_companies: e.target.value })} className={inputCls} /></td>
                  <td className="py-3 px-3">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">&pound;</span>
                      <input type="number" step="0.01" value={editForm.fee_pounds} onChange={(e) => setEditForm({ ...editForm, fee_pounds: e.target.value })} className={`${inputCls} pl-7`} />
                    </div>
                  </td>
                  <td className="py-3 px-3"><input type="text" value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} className={inputCls} /></td>
                  <td className="py-3 px-3 text-center space-x-2">
                    <button onClick={() => saveEdit(tier.id)} disabled={saving} className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50">Save</button>
                    <button onClick={cancelEdit} className="inline-flex items-center px-3 py-1.5 bg-slate-500 hover:bg-slate-600 text-white text-xs font-medium rounded-lg transition-colors">Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={tier.id} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                  <td className="py-3 px-3 text-slate-500 dark:text-slate-400">{tier.id}</td>
                  <td className="py-3 px-3 text-right text-slate-700 dark:text-slate-300">{tier.min_companies}</td>
                  <td className="py-3 px-3 text-right text-slate-700 dark:text-slate-300">{tier.max_companies}</td>
                  <td className="py-3 px-3 text-right font-medium text-slate-900 dark:text-white">{formatPence(tier.fee_pence)}</td>
                  <td className="py-3 px-3 text-slate-700 dark:text-slate-300">{tier.label}</td>
                  <td className="py-3 px-3 text-center">
                    <button onClick={() => startEdit(tier)} className="inline-flex items-center px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white text-xs font-medium rounded-lg transition-colors">Edit</button>
                  </td>
                </tr>
              )
            )}
            {addingNew && (
              <tr className="border-b border-slate-100 dark:border-slate-700/50 bg-emerald-50/50 dark:bg-emerald-900/10">
                <td className="py-3 px-3 text-slate-400 text-sm italic">New</td>
                <td className="py-3 px-3"><input type="number" value={newForm.min_companies} onChange={(e) => setNewForm({ ...newForm, min_companies: e.target.value })} className={inputCls} placeholder="0" /></td>
                <td className="py-3 px-3"><input type="number" value={newForm.max_companies} onChange={(e) => setNewForm({ ...newForm, max_companies: e.target.value })} className={inputCls} placeholder="10" /></td>
                <td className="py-3 px-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">&pound;</span>
                    <input type="number" step="0.01" value={newForm.fee_pounds} onChange={(e) => setNewForm({ ...newForm, fee_pounds: e.target.value })} className={`${inputCls} pl-7`} placeholder="300.00" />
                  </div>
                </td>
                <td className="py-3 px-3"><input type="text" value={newForm.label} onChange={(e) => setNewForm({ ...newForm, label: e.target.value })} className={inputCls} placeholder="Phase 1 (0-10 companies)" /></td>
                <td className="py-3 px-3 text-center space-x-2">
                  <button onClick={saveNew} disabled={saving} className="inline-flex items-center px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50">Create</button>
                  <button onClick={() => setAddingNew(false)} className="inline-flex items-center px-3 py-1.5 bg-slate-500 hover:bg-slate-600 text-white text-xs font-medium rounded-lg transition-colors">Cancel</button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!addingNew && (
        <div className="mt-4">
          <button
            onClick={() => { setAddingNew(true); setEditingId(null); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add New Tier
          </button>
        </div>
      )}
    </div>
  );
}

/* ========== Monthly Costs Tab ========== */

function MonthlyCostsTab() {
  const { openModal } = useModal();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [month, setMonth] = useState(defaultMonth);
  const [hosting, setHosting] = useState('');
  const [stripe, setStripe] = useState('');
  const [ai, setAi] = useState('');
  const [other, setOther] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchCosts = useCallback(async (m: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/billing/developer/costs/${m}`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success && d.costs) {
        setHosting(penceToPounds(d.costs.hosting_cost_pence || 0));
        setStripe(penceToPounds(d.costs.stripe_fee_pence || 0));
        setAi(penceToPounds(d.costs.ai_cost_pence || 0));
        setOther(penceToPounds(d.costs.other_cost_pence || 0));
        setNotes(d.costs.notes || '');
      } else {
        setHosting('0.00');
        setStripe('0.00');
        setAi('0.00');
        setOther('0.00');
        setNotes('');
      }
    } catch {
      setHosting('0.00');
      setStripe('0.00');
      setAi('0.00');
      setOther('0.00');
      setNotes('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCosts(month); }, [month, fetchCosts]);

  const totalVariable = poundsToPence(hosting) + poundsToPence(stripe) + poundsToPence(ai) + poundsToPence(other);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/billing/developer/costs', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          month,
          hosting_cost_pence: poundsToPence(hosting),
          stripe_fee_pence: poundsToPence(stripe),
          ai_cost_pence: poundsToPence(ai),
          other_cost_pence: poundsToPence(other),
          notes,
        }),
      });
      const d = await res.json();
      if (d.success !== false) {
        openModal({
          component: AlertModal,
          props: { title: 'Costs Saved', message: `Monthly costs for ${month} have been saved.`, type: 'success' },
        });
      } else {
        openModal({
          component: AlertModal,
          props: { title: 'Save Failed', message: d.error || 'Failed to save costs.', type: 'error' },
        });
      }
    } catch {
      openModal({
        component: AlertModal,
        props: { title: 'Error', message: 'Network error saving costs.', type: 'error' },
      });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
  const labelCls = 'block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Monthly Variable Costs</h2>

      <div className="mb-6">
        <label className={labelCls}>Settlement Month</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className={`${inputCls} w-48`}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div></div>
      ) : (
        <div className="space-y-4 max-w-lg">
          <div>
            <label className={labelCls}>Hosting &amp; Infrastructure (&pound;)</label>
            <input type="number" step="0.01" value={hosting} onChange={(e) => setHosting(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Stripe Processing Fees (&pound;)</label>
            <input type="number" step="0.01" value={stripe} onChange={(e) => setStripe(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>AI Provider Costs (&pound;)</label>
            <input type="number" step="0.01" value={ai} onChange={(e) => setAi(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Other Platform Costs (&pound;)</label>
            <input type="number" step="0.01" value={other} onChange={(e) => setOther(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={inputCls}
              placeholder="Optional notes about this month's costs..."
            />
          </div>

          {/* Live total */}
          <div className="bg-slate-50 dark:bg-slate-700/40 rounded-lg px-4 py-3 border border-slate-200 dark:border-slate-600">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Variable Cost</span>
              <span className="text-lg font-bold text-slate-900 dark:text-white">{formatPence(totalVariable)}</span>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 shadow-sm"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Saving...
              </>
            ) : (
              'Save Costs'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/* ========== Invoice Generation Tab ========== */

function InvoiceTab() {
  const { openModal } = useModal();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`/api/billing/developer/settlement/${month}/generate-invoice`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        const html = await res.text();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        openModal({
          component: AlertModal,
          props: {
            title: 'Invoice Generated',
            message: `Invoice for ${month} has been opened in a new tab. You can print to PDF from there.`,
            type: 'success',
          },
        });
      } else {
        const d = await res.json().catch(() => ({ error: 'Failed to generate invoice.' }));
        openModal({
          component: AlertModal,
          props: { title: 'Generation Failed', message: d.error || 'Failed to generate invoice.', type: 'error' },
        });
      }
    } catch {
      openModal({
        component: AlertModal,
        props: { title: 'Error', message: 'Network error generating invoice.', type: 'error' },
      });
    } finally {
      setGenerating(false);
    }
  };

  const inputCls = 'block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Generate Settlement Invoice</h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        Generate an HTML invoice for a settlement month. The invoice will open in a new browser tab where you can print it to PDF.
      </p>

      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Settlement Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={`${inputCls} w-48`}
          />
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating || !month}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 shadow-sm"
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Generating...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Generate Invoice
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/* ========== Audit Log Tab ========== */

function AuditLogTab() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/billing/developer/audit-log', { headers: authHeaders() });
        const d = await res.json();
        if (d.success && d.entries) setEntries(d.entries);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div></div>;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Billing Audit Log</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Timestamp</th>
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Actor</th>
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Action</th>
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Entity</th>
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">Before</th>
              <th className="text-left py-3 px-3 font-medium text-slate-500 dark:text-slate-400">After</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-400 dark:text-slate-500">
                  No audit log entries found.
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.id} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                  <td className="py-3 px-3 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                    {new Date(entry.timestamp).toLocaleString('en-GB')}
                  </td>
                  <td className="py-3 px-3 text-slate-700 dark:text-slate-300">{entry.actor}</td>
                  <td className="py-3 px-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      {entry.action}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-slate-700 dark:text-slate-300">{entry.entity}</td>
                  <td className="py-3 px-3 text-slate-500 dark:text-slate-400 text-xs font-mono max-w-[200px] truncate">
                    {entry.before_value || '\u2014'}
                  </td>
                  <td className="py-3 px-3 text-slate-500 dark:text-slate-400 text-xs font-mono max-w-[200px] truncate">
                    {entry.after_value || '\u2014'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
